import CKEditorInspector from '@ckeditor/ckeditor5-inspector';

const IFRAME_SRC = '//cdn.iframe.ly/api/iframe';
const API_KEY = process.env.MIX_IFRAME_APP_ID;

// InlineEditorConfig(inline 페이지)와 ClassicEditorConfig(/content/{contentId})가
// 같은 툴바/플러그인 설정을 쓰되 에디터 창작자(editor) 클래스만 다르도록 공유한다.
//
// editorGlobalName 은 'InlineEditor' | 'ClassicEditor' 문자열이다. app.js 는 모든
// 페이지에 공통으로 로드되며 components.js 가 두 에디터의 Vue 컴포넌트를 전부
// 등록하기 때문에, 실제로 window.InlineEditor / window.ClassicEditor 중 그 페이지가
// 로드한 CKEditor 번들만 존재한다. 클래스를 즉시(모듈 로드 시점) 참조하면 다른 쪽
// 전역이 없는 페이지에서 ReferenceError 가 나므로, 실제로 해당 컴포넌트가 쓰일 때
// (data() 호출 시점)까지 전역 조회를 미룬다.
export function createEditorConfigMixin(editorGlobalName) {
  return {
    props: ['items', 'removePlugins'],
    data() {
      return {
        editor: window[editorGlobalName],
        editorConfig: {
          forcePasteAsPlainText: 'allow-word',
          toolbar: {
            viewportTopOffset: 60,
            items: this.items || [
              'heading',
              'bold',
              'underline',
              'strikethrough',
              'subscript',
              'superscript',
              'big',
              'blockQuote',
              'quote',
              'bulletedList',
              'alignment',
              'link',
              'horizontalLine',
              'imageUpload',
              'imageHotspot',
              'mediaEmbed',
              'insertTable',
              'columns',
              'codeBlock',
              'details',
              'specialCharacters',
              'removeFormat',
              '|',
              'undo',
              'redo',
            ],
          },
          codeBlock: {
            languages: [
              { language: 'plaintext', label: 'Plain text' },
              { language: 'javascript', label: 'JavaScript' },
              { language: 'typescript', label: 'TypeScript' },
              { language: 'php', label: 'PHP' },
              { language: 'python', label: 'Python' },
              { language: 'ruby', label: 'Ruby' },
              { language: 'css', label: 'CSS' },
              { language: 'html', label: 'HTML' },
              { language: 'json', label: 'JSON' },
              { language: 'markdown', label: 'Markdown' },
              { language: 'shell', label: 'Shell' },
              { language: 'sql', label: 'SQL' },
              { language: 'yaml', label: 'YAML' },
            ],
          },
          link: {
            decorators: {
              markUnsafeLink: {
                mode: 'automatic',
                callback: url => url && !/(http(s?)):\/\//gi.test(url),
                attributes: {
                  class: 'unsafe-link',
                },
              },
              // 텍스트/이미지 링크 모두 링크 편집 화면에서 새 창으로 열기 여부를 직접 선택한다.
              openInNewTab: {
                mode: 'manual',
                label: '새 창에서 열기',
                defaultValue: false,
                attributes: {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
              },
            },
          },
          heading: {
            options: [
              { model: 'paragraph', title: '일반', class: 'ck-heading_paragraph' },
              { model: 'heading3', view: 'h3', title: '소제목', class: 'ck-heading_heading3' },
              {
                model: 'comment',
                view: {
                  name: 'p',
                  classes: ['editorial-comment'],
                },
                title: '코멘트',
                converterPriority: 'high',
              },
              {
                model: 'interview-question',
                view: {
                  name: 'p',
                  classes: ['editorial-interview-question'],
                },
                title: '인터뷰 질문',
                converterPriority: 'high',
              },
              {
                model: 'media-comment',
                view: {
                  name: 'p',
                  classes: ['media-comment'],
                },
                title: '미디어 코멘트',
                converterPriority: 'high',
              },
            ],
          },
          language: 'en',
          ckfinder: {
            uploadUrl: this.getUploadUrl(),
          },
          mediaEmbed: {
            previewsInData: false,
            extraProviders: [
              {
                name: 'slideshare',
                url: /^slideshare\.net/,
              },
              {
                name: 'instragram',
                url: /^instagram\.com\/reel/,

                html: match => {
                  const url = match[0];

                  var iframeUrl = IFRAME_SRC + '?api_key=' + API_KEY + '&url=' + encodeURIComponent(url);
                  console.log(iframeUrl);
                  return (
                    // If you need, set maxwidth and other styles for 'iframely-embed' class - it's yours to customize
                    '<div class="iframely-embed">' +
                    '<div class="iframely-responsive">' +
                    `<iframe src="${iframeUrl}" ` +
                    'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
                    '</iframe>' +
                    '</div>' +
                    '</div>'
                  );
                },
              },
            ],
          },
          table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
          },
          image: {
            // You need to configure the image toolbar, too, so it uses the new style buttons.
            toolbar: ['imageStyle:full', 'imageStyle:interviewee', '|', 'linkImage'],

            styles: [
              { name: 'full', title: 'Full', isDefault: true },

              { name: 'interviewee', title: 'Interviewee', className: 'editorial-image-interviewee' },
            ],
          },
          removePlugins: this.removePlugins || [],
        },
        guideItemTypeRadios: [
          {
            type: 1,
            name: '웹북/아티클',
          },
          {
            type: 2,
            name: '챕터',
          },
        ],
      };
    },
    computed: {},
    methods: {
      getUploadUrl() {
        return (
          location.protocol + '//' + location.hostname + '/api/file/upload2?CKEditor=fokjn&CKEditorFuncNum=1&langCode=ko'
        );
      },

      attachInspector: editor => {
        if (process.env.MIX_APP_ENV === 'local') {
          CKEditorInspector.attach(editor);
        }
      },
    },
  };
}
